import React from "react";
import { Link } from "react-router-dom";
import { Globe2, Shield, Home, Users, CheckCircle2, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import { AUDIENCES } from "../data/site";

const iconMap = { Globe2, Shield, Home, Users };

export default function WhoWeHelp() {
  useReveal();
  usePageMeta({
    title: "Who We Help",
    description: "Trauma-informed support for people affected by migration, abuse, homelessness, poverty and social exclusion.",
  });

  return (
    <>
      <PageHero
        eyebrow="Who we help"
        title="Who our service is for"
        lead="We support adults whose mental health has been affected by difficult life experiences, and the people and organisations supporting them."
      />

      <section className="container-page section-spacing">
        <div className="grid gap-6 md:grid-cols-2">
          {AUDIENCES.map((a) => {
            const Icon = iconMap[a.icon] || Users;
            return (
              <article
                key={a.title}
                className="fade-up bg-white border border-[var(--tb-border)] rounded-3xl p-8 md:p-10 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] mb-5">
                  <Icon className="h-5 w-5" />
                </span>
                <h2 className="font-serif-display text-2xl text-[var(--tb-text)] mb-3 text-balance">{a.title}</h2>
                <p className="text-[var(--tb-text-2)] text-pretty">{a.body}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Is this for me */}
      <section className="bg-[var(--tb-surface)] border-y border-[var(--tb-border)]">
        <div className="container-page section-spacing grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Is this for me?</span>
            <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)] text-balance">
              You don&rsquo;t need a diagnosis to get in touch
            </h2>
            <p className="mt-4 text-lg text-[var(--tb-text-2)] text-pretty">
              If any of the below applies to you, please get in touch. You don&rsquo;t have to be at breaking point to
              ask for support.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "I feel isolated or alone.",
                "I find it hard to trust services because of past experiences.",
                "Life feels overwhelming and I don't know where to start.",
                "I'm worried about someone I know and need help to support them.",
                "I want to talk to people who understand my background or culture.",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-[var(--tb-text)]">
                  <CheckCircle2 className="h-5 w-5 text-[var(--tb-primary)] mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link
                to="/get-support"
                data-testid="who-we-help-cta-button"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--tb-primary)] hover:bg-[var(--tb-primary-hover)] text-white px-6 py-3.5 font-semibold"
              >
                Get in touch <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-[var(--tb-border)]">
              <img
                src="https://images.unsplash.com/photo-1643012177761-cc4949dfa8ba?crop=entropy&cs=srgb&fm=jpg&w=900"
                alt="A person holding a warm cup of tea, looking calm and at ease"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
